import React from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import Search from "./search";
import QuoteData from "./quoteData";
import PriceChart from "./price-chart";
import Metrics from "./metrics";
import { getTwelveDataTimeSeries } from "../../services/api/http";

function parseNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default class FinancialRisk extends React.Component {
  constructor() {
    super();
    this.state = {
      instrument: null,
      autocompleteError: "",
      loadError: "",
      loading: false,
    };
  }

  getQuoteData = async (selected) => {
    const query = (selected || "").trim();

    if (!query) {
      this.setState({
        autocompleteError:
          "Please select an item from the search menu in order to continue.",
      });
      return;
    }

    let ticker = query;
    let lookupLabel = query;

    if (ticker.indexOf(":") > -1) {
      const parts = ticker.split(":");
      ticker = parts[0];
      lookupLabel = parts.slice(1).join(":").trim() || ticker;
    }

    ticker = ticker.toUpperCase();

    this.setState({ loading: true, loadError: "" });

    try {
      const resp = await getTwelveDataTimeSeries(ticker, "1day", 365);
      const series = resp?.data;
      const values = series?.values || [];
      if (!series || values.length === 0) {
        throw new Error("No market data returned");
      }

      const latest = values[0];
      const previous = values[1] || latest;
      const allHighs = values
        .map((entry) => parseNumber(entry.high))
        .filter((value) => value !== null);
      const allLows = values
        .map((entry) => parseNumber(entry.low))
        .filter((value) => value !== null);
      const price = parseNumber(latest.close);
      const previousClose = parseNumber(previous.close);
      const open = parseNumber(latest.open);
      const dayHigh = parseNumber(latest.high);
      const dayLow = parseNumber(latest.low);
      const change =
        price !== null && previousClose !== null ? price - previousClose : null;
      const changePercent =
        change !== null && previousClose
          ? (change / previousClose) * 100
          : null;
      const fiftyTwoWeekHigh = allHighs.length ? Math.max(...allHighs) : null;
      const fiftyTwoWeekLow = allLows.length ? Math.min(...allLows) : null;

      const instrument = {
        quoteData: {
          c: price,
          d: change,
          dp: changePercent,
          h: dayHigh,
          l: dayLow,
          o: open,
          pc: previousClose,
          symbol: series.meta?.symbol || ticker,
          name: series.meta?.name || lookupLabel || ticker,
          volume: parseNumber(latest.volume),
          fifty_two_week: {
            high: fiftyTwoWeekHigh,
            low: fiftyTwoWeekLow,
          },
        },
        metrics: {
          meta: series.meta || {},
          lookupLabel,
          symbol: series.meta?.symbol || ticker,
        },
      };

      this.setState({
        instrument,
        autocompleteError: "",
        loadError: "",
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        loadError: "I couldn't load that symbol. Try another ticker.",
      });
    }
  };

  render() {
    const { instrument, autocompleteError, loadError, loading } = this.state;
    return (
      <SiteLayout>
        <main className="page-shell home-v3-shell financial-risk-page">
          <div className="home-v3-frame">
            <PageIntro
              eyebrow="Technology / Market Risk"
              title="Prices, charts, metrics."
              description="Search a symbol, inspect the latest quote, and review market context in one focused workspace."
              visual={(
                <div className="home-v3-panel">
                  <span className="home-v3-kicker">Live</span>
                  <h3>{instrument?.quoteData?.symbol || "Select a symbol"}</h3>
                  <p>
                    {instrument?.quoteData?.name || "Quote, chart, and risk data"}
                  </p>
                </div>
              )}
            />

            <section className="home-v3-band" id="search">
              <div className="home-v3-band-label">
                <span className="home-v3-kicker">Search</span>
                <h2>Symbols.</h2>
              </div>
              <div className="home-v3-band-content">
                <div className="home-v3-panel">
                  <Search
                    onSearch={this.getQuoteData}
                    error={autocompleteError || loadError}
                  />
                  {loading ? (
                    <p className="meta-copy" style={{ marginTop: "14px" }}>
                      Loading market data...
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="home-v3-band">
              <div className="home-v3-band-label">
                <span className="home-v3-kicker">Quote</span>
                <h2>Snapshot.</h2>
              </div>
              <div className="home-v3-band-content">
                <div className="home-v3-panel">
                  <QuoteData instrument={instrument} />
                </div>
              </div>
            </section>

            <section className="home-v3-band">
              <div className="home-v3-band-label">
                <span className="home-v3-kicker">Chart</span>
                <h2>Trend.</h2>
              </div>
              <div className="home-v3-band-content">
                <div className="home-v3-panel">
                  {instrument ? (
                    <PriceChart ticker={instrument.quoteData.symbol} />
                  ) : null}
                </div>
              </div>
            </section>

            <section className="home-v3-band" id="metrics">
              <div className="home-v3-band-label">
                <span className="home-v3-kicker">Metrics</span>
                <h2>Risk context.</h2>
              </div>
              <div className="home-v3-band-content">
                <div className="home-v3-panel">
                  <Metrics instrument={instrument} />
                </div>
              </div>
            </section>
          </div>
        </main>
      </SiteLayout>
    );
  }
}
